import React, { useState, useEffect, FunctionComponent } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import Button from "@murphy-frontend/web-core/components/Button"
import MBSMessageEditor from "./MBSMessageEditor"
import MBSTemplateSelector from "./MBSTemplateSelector"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import ContactList, { ContactListModel } from "../../Contacts/components/ContactList"
import EditContact from "../../Contacts/components/EditContact"
import MBSConfirmation from "./MBSConfirmation"
import { useGetUsers } from "@murphy-frontend/common/api/Users/Queries"
import queryKeys from "../queryKeys"
import { useGetContacts, useGetCustomerContactOptionSets } from "../../../common/api/Contacts/queries"
import { useGetContactGroups } from "@murphy-frontend/common/api/ContactGroups/Queries"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { useCreateSendOut } from "../mutations"
import { CreateSendOutBatchRequest, Message } from "../MBSApi"
import { SendOutBatchTemplate, CustomAlertResponse } from "../models"
import {
	ContactModel,
	OptionSetModel,
	OptionSetViewModel,
	SelectedOptionSetViewModel
} from "../../../common/api/Contacts/ContactsApi"
import { MBSGroupMember, MBSRecipient } from "./models"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import MBSSearchRecipients from "./MBSSearchRecipients"
import MBSSelectedRecipients from "./MBSSelectedRecipients"
import CommunicationTypeFilter from "@murphy-frontend/web-core/components/CommunicationTypeFilter"
import { MurphyUser } from "@murphy-frontend/common/interfaces/IUsersApi"
import { ContactGroupModel } from "@murphy-frontend/common/interfaces/IContactGroupApi"
import { useUser } from "../../../common/contexts/UserContext"
import { Roles } from "@murphy-frontend/common/enums"
import { CustomCheckbox } from "../../../common/components/MurphyCheckbox"
import InfoIcon from "@murphy-frontend/web-core/components/InfoIcon"
import { OnboardingFlowIds, useOnboarding } from "../../../common/contexts/OnboardingContext"
import RichTextDisplay from "@murphy-frontend/web-core/features/RichText/RichTextDisplay"
import { parseHtml } from "@murphy-frontend/web-core/features/RichText/helpers"
import MBSCreateNewOnboardingSteps from "./MBSCreateNewOnboardingSteps"
import { enabledFeatures } from "../../../common/variables"
import ComponentWrapper from "../../../common/components/ComponentWrapper"
import TabPanel from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import { useGetCustomAlerts } from '../queries';
import ChannelSelection from "./ChannelSelection"
import MBSChooseRecipients from "./MBSChooseRecipients"
import Banner from "../../../common/Banner"

const MBSCreateNew: FunctionComponent = () => {
	const navigate = useNavigate()
	const { openModal, closeModal } = useModal();
	const { translations } = useUserPreferences()
	const { user } = useUser();
	const [selectedTabId, setSelectedTabId] = useState<number>(1);
	const {
		mutate: createSendOut,
		isPending: createSendOutIsLoading,
		isError: createSendOutIsError,
		error: createSendOutError
	} = useCreateSendOut()

	const [error, setError] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [users, setUsers] = useState<MBSRecipient[]>([])
	const [contacts, setContacts] = useState<MBSRecipient[]>([])
	const [contactGroups, setContactGroups] = useState<MBSRecipient[]>([])

	const [stepsEnabled, setStepsEnabled] = useState<boolean>(false)
	const [onboardingTerminated, setOnboardingTerminated] = useState<boolean>(null)
	const {
		setOnboardingIdIsComplete,
		incompletedOnboardingFlows,
		isLoading: onBoardingIsLoading,
		resetOnboarding,
		setCurrentStep,
		onboardingsCurrentStep
	} = useOnboarding();
	const [messageTemplateIsFlashing, setMessageTemplateIsFlashing] = useState<boolean>(false)

	const [notSelectedRecipients, setNotSelectedRecipients] = useState<MBSRecipient[]>([])
	const [selectedRecipients, setSelectedRecipients] = useState<MBSRecipient[]>([])
	const [showValidationErrors, setShowValidationErrors] = useState(false);
	const [mobileAllChecked, setMobileAllChecked] = useState(user?.Role === 3 ? false : true)
	const [emailAllChecked, setEmailAllChecked] = useState(true)
	const [notificationAllChecked, setNotificationAllChecked] = useState(true)
	const [criticalAlertIsChecked, setCriticalAlertIsChecked] = useState(false)
	const [geoLocationIsChecked, setGeoLocationIsChecked] = useState(false)

	const [messageBody, setMessageBody] = useState("")
	const [messageTitle, setMessageTitle] = useState("")
	const [messageQuestion, setMessageQuestion] = useState("")
	const [messageTitleError, setMessageTitleError] = useState("")
	const [messageBodyError, setMessageBodyError] = useState("")
	const [messageQuestionError, setMessageQuestionError] = useState("")
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
	const [selectedTemplate, setSelectedTemplate] = useState<SendOutBatchTemplate | CustomAlertResponse>();
	const [questionRequired, setQuestionRequired] = useState(false)

	const [optionSets, setOptionSets] = useState<OptionSetViewModel[]>([])

	const queryClient = useQueryClient()

	const templates: SendOutBatchTemplate[] = [
		{
			id: 1,
			title: translations.onewaycommunication,
			description: translations.onewaycomdescription,
			replyRequired: false,
			questionIncluded: false
		},
		{
			id: 2,
			title: translations.twowaycommunication,
			description: translations.twowaycomdescription,
			alternatives: [translations.yes, translations.no],
			replyRequired: true,
			questionIncluded: true
		}
	]

	const { customer } = useCustomer()
	const {
		isLoading: isLoadingUsers,
		isError: isErrorUsers,
		data: dataUsers,
		error: errorUsers
	} = useGetUsers(customer.Id)

	const {
		isLoading: isLoadingContacts,
		isError: isErrorContacts,
		data: dataContacts,
		error: errorContacts
	} = useGetContacts(customer.Id)

	const {
		isLoading: isLoadingContactGroups,
		isError: isErrorContactGroups,
		data: dataContactGroups,
		error: errorContactGroups
	} = useGetContactGroups(customer.Id)

	const {
		isLoading: isLoadingCustomerContactOptionSets,
		isError: isErrorCustomerContactOptionSets,
		data: dataCustomerContactOptionSets,
		error: errorCustomerContactOptionSets
	} = useGetCustomerContactOptionSets(customer.Id)

	const { data: customTemplates } = useGetCustomAlerts(customer?.Id);

	const createOptionSetViewModels = (data: OptionSetModel[]): OptionSetViewModel[] => {
		const mappedData = data.map(p => ({
			name: p.Name,
			id: p.ID,
			options: p.Options.map(q => ({
				id: q.ID,
				value: q.Value
			}))
		}))

		mappedData.sort((a, b) => a?.name?.localeCompare(b.name))
		return mappedData
	}

	const createUserRecipients = (data: MurphyUser[]): MBSRecipient[] => {
		const mappedUsers = data.map(
			p =>
				({
					uniqueKey: `${"White"}:${p.UserId}`,
					id: p.UserId,
					name: p.Username,
					email: p.Email,
					mobile: p.Phonenumber,
					deviceToken: p.DeviceToken,
					type: "internaluser",
					isGroup: false,
					mobileChecked: !!p.Phonenumber, // true if MobileNr exists and is not falsy
					emailChecked: !!p.Email,     // true if Email exists and is not falsy
					notificationChecked: !!p.DeviceToken, // true if DeviceToken exists and is not falsy
					checked: true
				}) as MBSRecipient
		)

		return mappedUsers
	}

	const createContactRecipients = (data: ContactModel[]): MBSRecipient[] => {
		const mappedContacts = data.map(
			p =>
				({
					uniqueKey: `${"internalcontact"}:${p.ID}`,
					id: p.ID,
					name: p.Name,
					email: p.Email,
					mobile: p.MobileNr,
					deviceToken: p.DeviceToken,
					type: "internalcontact",
					mobileChecked: !!p.MobileNr, // true if MobileNr exists and is not falsy
					emailChecked: !!p.Email,     // true if Email exists and is not falsy
					notificationChecked: !!p.DeviceToken, // true if DeviceToken exists and is not falsy
					checked: true,
					isGroup: false,
					selectedOptions: p.SelectedOptions
						? p.SelectedOptions.map(
							q =>
								({
									id: q.ID,
									value: q.Value,
									optionSetId: q.OptionSetId,
									optionSetName: q.OptionSetName
								}) as SelectedOptionSetViewModel
						)
						: []
				}) as MBSRecipient
		)
		return mappedContacts
	}

	const createContactGroupRecipients = (data: ContactGroupModel[]): MBSRecipient[] => {
		const mappedGroups = data
			.map(p => {
				let mappedContacts: MBSGroupMember[] = []
				let mappedUsers: MBSGroupMember[] = []

				if (p.ContactGroupContacts) {
					mappedContacts = p.ContactGroupContacts.map(q => ({
						name: q.Name,
						id: q.ID.toString(),
						email: q.Email,
						mobile: q.MobileNr,
						deviceToken: q.DeviceToken,
						mobileChecked: !!q.MobileNr,
						emailChecked: !!q.Email,
						notificationChecked: !!q.DeviceToken,
						type: "contact"
					}))
				}

				if (p.ContactGroupUsers) {
					mappedUsers = p.ContactGroupUsers.map(q => ({
						name: q.UserName,
						id: q.ID.toString(),
						customeruserid: q.CustomerUserId?.toUpperCase(),
						email: q.Email,
						mobile: q.MobilePhone,
						deviceToken: q.DeviceToken,
						mobileChecked: !!q.MobilePhone,
						emailChecked: !!q.Email,
						notificationChecked: !!q.DeviceToken,
						type: "user"
					}))
				}

				const allGroupMembers = [...mappedUsers, ...mappedContacts]

				if (allGroupMembers.length === 0) return null

				return {
					uniqueKey: `internalgroup:${p.ID}`,
					id: p.ID,
					name: p.Name,
					email: `${p.Name} (${translations.group})`,
					mobile: `${p.Name} (${translations.group})`,
					deviceToken: `${p.Name} (${translations.group})`,
					type: "internalgroup",
					mobileChecked: true,
					emailChecked: true,
					notificationChecked: true,
					checked: true,
					isGroup: true,
					groupMembers: allGroupMembers
				} as MBSRecipient
			})
			.filter(Boolean) // removes null entries
		return mappedGroups
	}

	const onFail = errorData => {
		if (errorData.statusText) {
			setError(errorData.statusText)
		} else {
			setError("Error")
		}
	}

	const onSuccess = () => {
		setIsLoading(false)
		setMessageBody("")
		setMessageTitle("")
		queryClient.invalidateQueries({ queryKey: queryKeys.all })
		navigate("/pbslist")
		setIsLoading(false)
	}

	const showConfirmModal = () => {
		const recipients = notSelectedRecipients.filter(
			p =>
				p.isAdded &&
				((p.mobileChecked && p.mobile) ||
					(p.emailChecked && p.email) ||
					(p.notificationChecked && p.deviceToken))
		)
		const modalConf: ModalConfiguration = {
			title: translations.sendalert || "Send alert?",
			body: <div style={{ width: '30rem' }}>
				<label>{`Clicking Send will send an alert to ${recipients.length} recipients. Make sure you have review the alert before sending.`}</label>
				<div style={{ flexDirection: 'row', display: 'flex', justifyContent: 'center', marginTop: 20 }}>
					<div className="modal-footer">
						<Button buttonStyle="btn--primary" buttonSize="btn--medium" onClick={() => closeModal()}>{translations.cancel}</Button>
					</div>
					<div className="modal-footer">
						<Button buttonStyle="btn--primary" buttonSize="btn--medium" onClick={submit}>{translations.send || "Send"}</Button>
					</div>

				</div>
			</div>,
			type: "info",
			position: "center",
			hideButton: true
		}

		openModal(modalConf)
	}

	const submit = () => {
		closeModal();
		setIsLoading(true)
		const recipients = notSelectedRecipients.filter(
			p =>
				p.isAdded &&
				((p.mobileChecked && p.mobile) ||
					(p.emailChecked && p.email) ||
					(p.notificationChecked && p.deviceToken))
		)

		const personRecipients = recipients
			.filter(p => !p.isGroup)
			.map(q => ({
				ID: q.id.toString(),
				Username: q.id.toString(),
				origin: q.type,
				Email: !!q.emailChecked,
				MobileNr: !!q.mobileChecked,
				DeviceToken: !!q.notificationChecked
			}));

		const groupRecipients = recipients
			.filter(p => p.isGroup === true)
			.map(q => ({
				GroupId: q.id.toString(),
				Email: !!q.emailChecked,
				MobileNr: !!q.mobileChecked,
				DeviceToken: !!q.notificationChecked,
				origin: q.type
			}))

		const message: Message = {
			Title: messageTitle,
			Body: messageBody
		}
		let alternatives = null
		if (questionRequired) {
			if (selectedTemplate.alternatives || selectedTemplate.ResponseAlternatives) {
				alternatives = selectedTemplate.alternatives
					? [...selectedTemplate.alternatives]
					: selectedTemplate.ResponseAlternatives;
			}
		}

		if (questionRequired) {
			message.Question = messageQuestion
		}

		const createSendOutRequest: CreateSendOutBatchRequest = {
			licenseId: customer.Id,
			recipiants: personRecipients,
			grouprecipiants: groupRecipients,
			message: message,
			alternatives: questionRequired ? alternatives : undefined,
			criticalAlert: criticalAlertIsChecked,
			geoLocation: geoLocationIsChecked
		}

		createSendOut(createSendOutRequest, {
			onSuccess: () => {
				onSuccess()
			},
			onError: err => {
				onFail(err.response)
			}
		})
	}

	const onSelectTemplate = (templateId: number) => {
		const newId = `default-${templateId}`;
		setSelectedTemplateId(newId);

		const selectedTemplate = templates.find(p => p.id === templateId);
		if (!selectedTemplate) return;

		setSelectedTemplate(selectedTemplate);
		setQuestionRequired(selectedTemplate.replyRequired);


		// Nollställ kanalval
		setEmailAllChecked(false);
		setMobileAllChecked(false);
		setNotificationAllChecked(false);
		setCriticalAlertIsChecked(false);

		// Nollställ kanaler för varje mottagare
		const newNotSelectedRecipients = notSelectedRecipients.map(item => ({
			...item,
			mobileChecked: false,
			emailChecked: false,
			notificationChecked: false,
			isAdded: false
		}));

		setNotSelectedRecipients(newNotSelectedRecipients);

		// Rensa/bestäm meddelandeinnehåll
		setMessageTitle(selectedTemplate?.Name || "");
		setMessageBody(selectedTemplate?.Body || "");
		setMessageQuestion(selectedTemplate?.Question || "");
	};

	const onSelectCustomTemplate = (templateId: number) => {
		const newId = `custom-${templateId}`;
		setSelectedTemplateId(newId);

		const selectedTemplate = customTemplates.find(p => p.Id === templateId);

		if (!selectedTemplate) return;

		setSelectedTemplate(selectedTemplate);
		setQuestionRequired(selectedTemplate?.ResponseRequired);

		// Set channels
		setEmailAllChecked(selectedTemplate?.MessageModes.includes('Email'));
		setMobileAllChecked(selectedTemplate?.MessageModes.includes('Sms'));
		setNotificationAllChecked(selectedTemplate?.MessageModes.includes('Notification'));
		setCriticalAlertIsChecked(selectedTemplate?.CriticalAlertEnabled);

		// Set message content
		setMessageTitle(selectedTemplate?.Subject);
		setMessageBody(selectedTemplate?.Body);
		setMessageQuestion(selectedTemplate?.Question);

		const templateRecipients = selectedTemplate.Recipients || [];

		// Build a lookup for matching
		const recipientLookup: Record<string, boolean> = {};
		templateRecipients.forEach(rec => {
			if (rec.CustomerUserId) {
				recipientLookup[`user:${rec.CustomerUserId.toLowerCase()}`] = true;
			}
			if (rec.contactId !== null && rec.contactId !== undefined) {
				recipientLookup[`contact:${rec.contactId}`] = true;
			}
			if (rec.contactGroupId !== null && rec.contactGroupId !== undefined) {
				recipientLookup[`group:${rec.contactGroupId}`] = true;
			}
		});

		// Normalize internalgroup members with user/contact data
		const normalizeRecipient = (r: MBSRecipient): MBSRecipient => {
			if (r.isGroup && r.groupMembers?.length) {
				const normalizedMembers = r.groupMembers.map(member => {
					const key = member.type === 'user'
						? `user:${member.customeruserid?.toLowerCase?.()}`
						: `contact:${member.id}`;

					return {
						...member,
						uniqueKey: key,
						isGroup: false,
						checked: true
					};
				});
				return { ...r, groupMembers: normalizedMembers };
			}
			return r;
		};

		const updatedRecipients = notSelectedRecipients.map(item => {
			const normalized = normalizeRecipient(item);
			let matchKey = '';

			if (normalized.type === 'internaluser') {
				matchKey = `user:${normalized.id?.toString().toLowerCase()}`;
			} else if (normalized.type === 'internalcontact') {
				matchKey = `contact:${normalized.id}`;
			} else if (normalized.type === 'internalgroup') {
				matchKey = `group:${normalized.id}`;
			}

			const isAdded = !!recipientLookup[matchKey];

			return {
				...normalized,
				isAdded,
				emailChecked: selectedTemplate?.MessageModes.includes('Email'),
				mobileChecked: selectedTemplate?.MessageModes.includes('Sms'),
				notificationChecked: selectedTemplate?.MessageModes.includes('Notification'),
			};
		});

		setNotSelectedRecipients(updatedRecipients);
	};

	useEffect(() => {
		if (stepsEnabled && selectedTemplateId !== null) {
			setMessageTemplateIsFlashing(false)
		}
	}, [selectedTemplateId, stepsEnabled])

	function NoUsers() {
		return <p>{translations.nousersselected}</p>
	}

	function NoTemplate() {
		return <p>{translations.notemplateselected}</p>
	}

	function ErrorMessage() {
		return <p>{error}</p>
	}

	const onClickRecipient = (recipient: MBSRecipient) => {
		const type = recipient.type
		const clickedId = recipient.id

		if (type === "internalgroup") {
			const contactGroupToDisplay = contactGroups.filter(p => p.id == clickedId)[0]
			const contactGroupTitle = `${translations.contactgroup} - ${contactGroupToDisplay.name}`
			const mappedContacts = contactGroupToDisplay.groupMembers.map(p => {
				return {
					id: p.id,
					name: p.name,
					email: p.email,
					mobile: p.mobile,
					deviceToken: p.deviceToken,
					type: p.type
				} as ContactListModel
			})

			const contactList = (
				<div>
					<h3>{translations.groupmembers}</h3>
					<ContactList
						translations={translations}
						contacts={mappedContacts}
						showEdit={false}
						showDelete={false}
						showAdd={false}
					/>
				</div>
			)

			const modalConf: ModalConfiguration = {
				title: contactGroupTitle,
				body: contactList,
				buttonText: "Ok",
				type: "info",
				position: 'center',
				centerContent: false
			}

			openModal(modalConf)
		} else if (type === "internaluser") {
			const userToDisplay = users.filter(p => p.id == clickedId)[0]
			const userTitle = `${translations.user} - ${userToDisplay.name}`

			const modalConf: ModalConfiguration = {
				title: userTitle,
				body: (
					<EditContact
						translations={translations}
						customerId={customer.Id}
						contactId={userToDisplay.id}
						nameInit={userToDisplay.name}
						emailInit={userToDisplay.email}
						mobileInit={userToDisplay.mobile}
						displayOnly
					/>
				),
				hideButton: true,
				position: 'center',
				type: "info"
			}

			openModal(modalConf)
		} else if (type === "internalcontact") {
			const contactToDisplay = contacts.filter(p => p.id == clickedId)[0]
			const contactTitle = `${translations.contact} - ${contactToDisplay.name}`

			const modalConf: ModalConfiguration = {
				title: contactTitle,
				body: (
					<EditContact
						translations={translations}
						customerId={customer.Id}
						contactId={contactToDisplay.id}
						nameInit={contactToDisplay.name}
						emailInit={contactToDisplay.email}
						mobileInit={contactToDisplay.mobile}
						selectedOptions={contactToDisplay.selectedOptions}
						optionSets={optionSets}
						displayOnly={true}
					/>
				),
				hideButton: true,
				position: 'center',
				type: "info"
			}
			openModal(modalConf)
		}
	}

	const onCheckAllMobile = () => {
		const newSelectedRecipients = selectedRecipients.map(item => {
			const updatedItem = {
				...item,
				mobileChecked: !mobileAllChecked
			}
			return updatedItem
		})
		setSelectedRecipients(newSelectedRecipients)

		const newNotSelectedRecipients = notSelectedRecipients.map(item => {
			const updatedItem = {
				...item,
				mobileChecked: !mobileAllChecked
			}
			return updatedItem
		})
		setNotSelectedRecipients(newNotSelectedRecipients)

		setMobileAllChecked(!mobileAllChecked)
	}

	const onCheckAllEmail = () => {
		const newSelectedRecipients = selectedRecipients.map(item => {
			const updatedItem = {
				...item,
				emailChecked: !emailAllChecked
			}
			return updatedItem
		})
		setSelectedRecipients(newSelectedRecipients)

		const newNotSelectedRecipients = notSelectedRecipients.map(item => {
			const updatedItem = {
				...item,
				emailChecked: !emailAllChecked
			}
			return updatedItem
		})
		setNotSelectedRecipients(newNotSelectedRecipients)
		setEmailAllChecked(!emailAllChecked)
	}

	const onCheckAllNotification = () => {
		const newSelectedRecipients = selectedRecipients.map(item => {
			const updatedItem = {
				...item,
				notificationChecked: !notificationAllChecked
			}
			return updatedItem
		})
		setSelectedRecipients(newSelectedRecipients)

		const newNotSelectedRecipients = notSelectedRecipients.map(item => {
			const updatedItem = {
				...item,
				notificationChecked: !notificationAllChecked
			}
			return updatedItem
		})
		setNotSelectedRecipients(newNotSelectedRecipients)
		setNotificationAllChecked(!notificationAllChecked)
	}

	const onMessageBodyChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.value) {
			setMessageBodyError(translations.mandatory)
		} else {
			setMessageBodyError("")
		}
		setMessageBody(event.target.value)
	}

	const onMessageTitleChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.value) {
			setMessageTitleError(translations.mandatory)
		} else {
			setMessageTitleError("")
		}
		setMessageTitle(event.target.value)
	}

	const onMessageQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.value) {
			setMessageQuestionError(translations.mandatory)
		} else {
			setMessageQuestionError("")
		}
		setMessageQuestion(event.target.value)
	}

	const addRecipient = (recipient: MBSRecipient): void => {
		const newNotSelectedRecipients = notSelectedRecipients.map(item => {
			const updatedItem = {
				...item,
				isAdded: recipient.uniqueKey === item.uniqueKey ? !item.isAdded : item.isAdded
			}
			return updatedItem
		})
		setNotSelectedRecipients(newNotSelectedRecipients)
	}

	const removeRecipient = (recipient: MBSRecipient): void => {
		const newRecipient = { ...recipient, isAdded: false };
		const notSelectedRecipientsCopy = [...notSelectedRecipients, newRecipient]
		setNotSelectedRecipients(notSelectedRecipientsCopy)
	}

	const onCheckCriticalAlert = () => {
		setCriticalAlertIsChecked(!criticalAlertIsChecked)
	}

	useEffect(() => {
		if (isLoadingContactGroups === false) {
			const contactGroupRecipients = createContactGroupRecipients(dataContactGroups)
			setContactGroups(contactGroupRecipients)
		}
		return () => {
			setContactGroups([])
		}
	}, [isLoadingContactGroups, dataContactGroups])

	useEffect(() => {
		if (isLoadingUsers === false) {
			const userRecipients = createUserRecipients(dataUsers)
			setUsers(userRecipients)
		}
		return () => {
			setUsers([])
		}
	}, [isLoadingUsers, dataUsers])

	useEffect(() => {
		if (isLoadingContacts === false) {
			const contactRecipients = createContactRecipients(dataContacts)
			setContacts(contactRecipients)
		}
		return () => {
			setContacts([])
		}
	}, [isLoadingContacts, dataContacts])

	useEffect(() => {
		if (isLoadingCustomerContactOptionSets === false) {
			const optionSetViewModels = createOptionSetViewModels(dataCustomerContactOptionSets)
			setOptionSets(optionSetViewModels)
		}
	}, [isLoadingCustomerContactOptionSets, dataCustomerContactOptionSets])

	useEffect(() => {
		const allRec = [
			...contactGroups.sort((a, b) => a?.name?.localeCompare(b.name)),
			...users.sort((a, b) => a?.name?.localeCompare(b.name)),
			...contacts.sort((a, b) => a?.name?.localeCompare(b.name))
		]
		setNotSelectedRecipients([...allRec])
	}, [users, contactGroups, contacts])

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError(null)
				}
			}

			openModal(modalConf)
		}
	}, [error])

	useEffect(() => {
		if (onboardingTerminated === true) {
			return
		}
		const shouldShowOnboarding = incompletedOnboardingFlows
			.map(p => p.OnboardingFlowId)
			.includes(OnboardingFlowIds.Alert)
		if (shouldShowOnboarding && !onBoardingIsLoading) {
			const currentStep = onboardingsCurrentStep.get(OnboardingFlowIds.Alert)
			if (currentStep === 0) {
				const stepsFromApi = incompletedOnboardingFlows.find(
					p => p.OnboardingFlowId === OnboardingFlowIds.Alert
				)?.StepsHtmlContents
				const introText = stepsFromApi.get(1)
				const parsedHtmlIntro = introText ? parseHtml(introText) : ""
				const onClickOk = () => {
					setCurrentStep(OnboardingFlowIds.Alert, 1)
					closeModal()
					setStepsEnabled(true)
				}

				const onClickClose = () => {
					setOnboardingIsComplete()
				}

				const modalConf: ModalConfiguration = {
					okCallback: onClickOk,
					onCloseModal: onClickClose,
					type: "info",
					centerContent: true,
					body: (
						<div className='intro-outro-container'>
							<RichTextDisplay content={parsedHtmlIntro} />
						</div>
					),
					title: "Tutorial",
					buttonText: "Start",
					position: "center"
				}
				openModal(modalConf)
			} else {
				setStepsEnabled(true)
			}
		}
	}, [incompletedOnboardingFlows, onBoardingIsLoading, onboardingsCurrentStep])

	const handleOnboardingComplete = () => {
		setOnboardingIsComplete()
	}

	const setOnboardingIsComplete = () => {
		setOnboardingTerminated(true)
		setOnboardingIdIsComplete(OnboardingFlowIds.Alert)
		setCurrentStep(OnboardingFlowIds.Alert, 0)
		setStepsEnabled(false)
	}

	if (
		isLoading ||
		isLoadingContactGroups ||
		isLoadingContacts ||
		isLoadingCustomerContactOptionSets ||
		isLoadingUsers ||
		createSendOutIsLoading
	) {
		return (
			<ComponentWrapper active={2} subMenuActive={1}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	const onClickStartOnboarding = () => {
		setOnboardingTerminated(false)
		resetOnboarding(OnboardingFlowIds.Alert)
	}

	const stepsComponent = (
		<MBSCreateNewOnboardingSteps
			stepsEnabled={stepsEnabled}
			onComplete={handleOnboardingComplete}
			setStepsEnabled={setStepsEnabled}
			setMessageTemplateIsFlashing={setMessageTemplateIsFlashing}
			messageTemplateIsSelected={!!selectedTemplateId}
		/>
	)

	const stepsIcon = (
		<InfoIcon
			id='info-onboarding-mbs'
			place={"right-end"}
			onHoverContents={<Button onClick={onClickStartOnboarding}>Start tutorial</Button>}
			clickable={true}
		/>
	)

	const tabs = [
		{
			id: 1,
			displayText: translations.typ || "Type",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		},
		{
			id: 2,
			displayText: translations.channels || "Channel(s)",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		},
		{
			id: 3,
			displayText: translations.message || "Message",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		},
		{
			id: 4,
			displayText: translations.recipients || "Recipients",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		},
		{
			id: 5,
			displayText: translations.confirmation || "Confirmation",
			customClass: "tab-transparent-panel-tab",
			customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
		}
	]

	const onClickTab = (tabId: number) => {
		if (tabId === tabs.length) {
			setShowValidationErrors(!canProceedToSubmit);
		} else {
			setShowValidationErrors(false);
		}
		setSelectedTabId(tabId);
	};


	const onNext = () => {
		const isCustomTemplate = selectedTemplate && ![1, 2].includes(selectedTemplate.id);
		const isLastTab = selectedTabId === tabs.length;

		if (isLastTab) {
			if (!canProceedToSubmit) {
				setShowValidationErrors(true);
				return;
			}
			showConfirmModal();
		} else {
		  let nextTabId = selectedTabId + 1;
	  
		  if (
			isCustomTemplate &&
			!selectedTemplate.startFromBeginning
		) {
			if (
				!(mobileAllChecked || emailAllChecked || notificationAllChecked)
			) {
				nextTabId = 2;
			} else if (
				!messageBody ||
				(selectedTemplate?.ResponseRequired && !messageQuestion)
			) {
				nextTabId = 3;
			} else if (notSelectedRecipients?.filter(item => item.isAdded).length === 0) {
				nextTabId = 4;
			} else {
				nextTabId = tabs.length;
			}
		}
	  
		  if (nextTabId === tabs.length) {
			setShowValidationErrors(!canProceedToSubmit);
		  } else {
			setShowValidationErrors(false);
		  }
	  
		  setSelectedTabId(nextTabId);
		}
	};


	const onBack = () => {
		setSelectedTabId(selectedTabId - 1);
	}

	const validRecipients = notSelectedRecipients.filter(
		p =>
			p.isAdded &&
			((p.mobileChecked && p.mobile) ||
				(p.emailChecked && p.email) ||
				(p.notificationChecked && p.deviceToken))
	)

	const hasRecipients = validRecipients.length > 0
	const hasMessageTitle = !!messageTitle?.trim()
	const hasMessageBody = !!messageBody?.trim()
	const hasAnyChannel = mobileAllChecked || emailAllChecked || notificationAllChecked

	const canProceedToSubmit = hasRecipients && hasMessageTitle && hasMessageBody && hasAnyChannel

	const renderTabBody = () => {
		switch (selectedTabId) {
			case 1:
				return (
					<MBSTemplateSelector
						templates={templates}
						onSelectTemplate={onSelectTemplate}
						customTemplates={customTemplates}
						onSelectCustomTemplate={onSelectCustomTemplate}
						selectedId={selectedTemplateId}
						translations={translations}
						messageTemplateIsFlashing={messageTemplateIsFlashing}
					/>
				);
			case 2:
				return (
					<ChannelSelection
						onCheckAllMobile={onCheckAllMobile}
						onCheckAllEmail={onCheckAllEmail}
						onCheckAllNotification={onCheckAllNotification}
						onCheckCriticalAlert={onCheckCriticalAlert}
						checkAllMobileChecked={mobileAllChecked}
						checkAllEmailChecked={emailAllChecked}
						checkAllNotificationChecked={notificationAllChecked}
						criticalAlertIsChecked={criticalAlertIsChecked}
						smsDisabled={user?.Role === Roles.Trial || enabledFeatures.mbs.disableSMS === "true"}
						translations={translations}
						notificationDisabled={enabledFeatures.mbs.disableNotification === "true"}
						emailDisabled={enabledFeatures.mbs.disableEmail === "true"}
					/>
				);
			case 3:
				return (
					<MBSMessageEditor
						translations={translations}
						handleMessageBodyChange={onMessageBodyChanged}
						handleMessageTitleChange={onMessageTitleChanged}
						handleQuestionChange={onMessageQuestionChange}
						messageTitleError={messageTitleError}
						messageBodyError={messageBodyError}
						messageQuestionError={messageQuestionError}
						message={messageBody}
						title={messageTitle}
						question={messageQuestion}
						showQuestionEditor={questionRequired}
						rows={5}
						cols={20}
						showNoOfSms={mobileAllChecked}
						hideOptions={true}
					/>
				);
			case 4:
				return (
					<MBSChooseRecipients
						translations={translations}
						recipients={notSelectedRecipients}
						contactGroups={contactGroups}
						addRecipient={addRecipient}
						clickRecipient={onClickRecipient}
					/>
				);
			case 5:
				const flatRecipients: MBSRecipient[] = notSelectedRecipients
					.filter(p => p.isAdded)
					.reduce((acc, p) => {
						if (p.isGroup && p.groupMembers?.length) {
							const mappedMembers: MBSRecipient[] = p.groupMembers.map(member => ({
								...member,
								name: member.name,
								id: member.id,
								email: member.email,
								mobile: member.mobile,
								deviceToken: member.deviceToken,
								mobileChecked: member.mobileChecked,
								emailChecked: member.emailChecked,
								notificationChecked: member.notificationChecked,
								uniqueKey: `${member.type}:${member.id}`,
								type: member.type,
								isGroup: false,
								checked: true,         // <- required by MBSRecipient
								isAdded: true,         // <- you probably want them included
							}));
							return acc.concat(mappedMembers);
						}

						return acc.concat({
							...p,
							checked: true,           // ensure required fields
							isAdded: true,
						});
					}, [] as MBSRecipient[])
					.filter(p =>
						(p.mobileChecked && p.mobile) ||
						(p.emailChecked && p.email) ||
						(p.notificationChecked && p.deviceToken)
					);

				return (
					<MBSConfirmation
						translations={translations}
						messageTitle={messageTitle}
						messageBody={messageBody}
						messageQuestion={messageQuestion}
						onClickRecipient={onClickRecipient}
						questionRequired={questionRequired}
						mobileChecked={mobileAllChecked}
						notificationChecked={notificationAllChecked}
						emailChecked={emailAllChecked}
						recipients={flatRecipients}
						criticalAlert={criticalAlertIsChecked}
						showValidationErrors={showValidationErrors}
					/>
				);
		}
	};

	// ... [rest of the component remains unchanged]

	return (
		<ComponentWrapper active={2} subMenuActive={1}>
			<div className="pbs-wrapper">
				<h1 className="heading">{translations.createalert || "Create new alert"}</h1>
				{customer.IsFreemium && <Banner 
					image={require('../../../assets/img/alert_banner.png')} 
					title={'Get Critical Messages Out - Fast'}
					body={"Use alerts to keep your team informed and aligned during high-pressure situations.Whether you're sending updates or requesting a response, this tool ensures your message gets through quickly and clearly."}
					subBody = {"Start by selecting an alert type below."}
				/>}
				<div style={{ width: "80rem", backgroundColor: "#E0E1DD", height: "5px" }}>
					<div
						style={{
							backgroundColor: "#00A098",
							width: `${(selectedTabId / tabs.length) * 80}rem`,
							height: "5px",
						}}
					/>
				</div>
				<div className="container">
					<TabPanel
						tabs={tabs}
						onClickTab={onClickTab}
						clickable={true}
						selectedTabId={selectedTabId}
						customClass="tab-solid-panel-tms"
					/>
					<div style={{ height: "50rem" }}>
						{renderTabBody()}
						<div style={{ flexDirection: "row", marginTop: 30, display: "flex", paddingLeft: 20 }}>
							{selectedTabId > 1 && (
								<Button
									buttonStyle="btn--primary"
									buttonSize="btn--medium"
									onClick={onBack}
								>
									{translations.back || "Back"}
								</Button>
							)}
							<div style={{ marginLeft: 20 }}>
								<Button
									buttonStyle="btn--primary"
									buttonSize="btn--medium"
									onClick={onNext}
									disabled={selectedTabId === tabs.length && !canProceedToSubmit}
								>
									{selectedTabId === tabs.length
										? translations.proceed || "Proceed to Send"
										: translations.nextBox || "Next"}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ComponentWrapper>
	);
};

export default MBSCreateNew;
